import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import CreateOrganizationModal from './CreateOrganizationModal';
import InviteUserModal from './InviteUserModal';
import InviteLinkModal from './InviteLinkModal';
import UserDeleteModal from './UserDeleteModal';
import ManageOrgAdminsModal from './ManageOrgAdminsModal';
import OrganizationComponentSettingsModal from './OrganizationComponentSettingsModal';
import OrganizationDeleteModal from './OrganizationDeleteModal';
import SSODomainModal from './SSODomainModal';
import FlightAwareLogs from './FlightAwareLogs';
import AIUsageLogs from './AIUsageLogs';
import RejectedEmailLogs from './RejectedEmailLogs';
import PackageFormModal from './PackageFormModal';
import AssignPackageModal from './AssignPackageModal';
import ParsingFeedbackAdmin from './ParsingFeedbackAdmin';
import { listEmailDomains, addEmailDomain, updateEmailDomain, setPrimaryDomain, deleteEmailDomain } from '../../services/emailDomainService';

const SuperadminDashboard = () => {
  const [activeTab, setActiveTab] = useState('organizations');
  const [organizations, setOrganizations] = useState([]);
  const [deletedOrganizations, setDeletedOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [packages, setPackages] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showOrgDeleteModal, setShowOrgDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [showManageAdminsModal, setShowManageAdminsModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showComponentSettingsModal, setShowComponentSettingsModal] = useState(false);
  const [selectedOrgForSettings, setSelectedOrgForSettings] = useState(null);
  const [showSSODomainModal, setShowSSODomainModal] = useState(false);
  const [selectedOrgForSSO, setSelectedOrgForSSO] = useState(null);
  // Package management state
  const [showPackageFormModal, setShowPackageFormModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAssignPackageModal, setShowAssignPackageModal] = useState(false);
  // User management state
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  // Email domains state
  const [emailDomains, setEmailDomains] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadData();
    }
  }, [user, activeTab, showInactiveUsers]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const requests = [
        axios.get(`${apiBase}/admin/organizations`, { headers }),
        axios.get(`${apiBase}/admin/users${showInactiveUsers ? '?include_inactive=true' : ''}`, { headers }),
        axios.get(`${apiBase}/admin/invites`, { headers }),
        axios.get(`${apiBase}/admin/packages`, { headers }),
        axios.get(`${apiBase}/admin/system-settings`, { headers })
      ];

      // Load deleted organizations if on that tab
      if (activeTab === 'deleted') {
        requests.push(axios.get(`${apiBase}/admin/organizations/deleted`, { headers }));
      }

      const responses = await Promise.all(requests);
      const [orgsResponse, usersResponse, invitesResponse, packagesResponse, settingsResponse, deletedOrgsResponse] = responses;

      setOrganizations(orgsResponse.data);
      setUsers(usersResponse.data);
      setInvites(invitesResponse.data);
      setPackages(packagesResponse.data);
      setSystemSettings(settingsResponse.data);

      if (deletedOrgsResponse) {
        setDeletedOrganizations(deletedOrgsResponse.data);
      }

      // Load email domains if on domains tab
      if (activeTab === 'domains') {
        try {
          const domains = await listEmailDomains(true);
          setEmailDomains(domains);
        } catch (domainErr) {
          console.error('Error loading email domains:', domainErr);
        }
      }
    } catch (err) {
      console.error('Error loading superadmin data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (orgData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(`${apiBase}/admin/organizations`, orgData, { headers });
      setShowCreateOrgModal(false);
      await loadData(); // Reload data
    } catch (err) {
      console.error('Failed to create organization in dashboard:', err);
      setError('Failed to create organization: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInviteUser = async (inviteData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(`${apiBase}/admin/invite-user`, inviteData, { headers });
      setShowInviteUserModal(false);
      
      // Show success with invite link modal
      if (response.data.status === 'approved') {
        setError(''); // Clear any previous errors
        
        // Find organization name
        const organization = organizations.find(org => org.id === inviteData.organization_id);
        const organizationName = organization ? organization.name : 'the organization';
        
        // Set up invite result for the modal
        setInviteResult({
          inviteLink: response.data.invite_link,
          inviteToken: response.data.invite_token,
          email: inviteData.email,
          organizationName: organizationName,
          role: inviteData.role,
          emailSent: response.data.email_sent || false
        });

        setShowInviteLinkModal(true);
      }

      loadData();
    } catch (err) {
      console.error('Failed to invite user:', err);
      setError('Failed to invite user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleApproveInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/approve-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to approve invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/reject-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to reject invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleManageOrgAdmins = (organization) => {
    setSelectedOrganization(organization);
    setShowManageAdminsModal(true);
  };

  const handleComponentSettings = (org) => {
    setSelectedOrgForSettings(org);
    setShowComponentSettingsModal(true);
  };

  const handleSSODomains = (org) => {
    setSelectedOrgForSSO(org);
    setShowSSODomainModal(true);
  };

  // Package Management Handlers
  const handleSavePackage = async (packageData) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    if (selectedPackage) {
      // Update existing package
      await axios.put(`${apiBase}/admin/packages/${selectedPackage.id}`, packageData, { headers });
    } else {
      // Create new package
      await axios.post(`${apiBase}/admin/packages`, packageData, { headers });
    }

    await loadData();
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to archive this package? It will no longer be available for new assignments.')) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/packages/${packageId}`, { headers });
      await loadData();
    } catch (err) {
      setError('Failed to archive package: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmins = async () => {
    setShowManageAdminsModal(false);
    setSelectedOrganization(null);
    await loadData();
  };

  const handleDeleteOrganization = async (orgId) => {
    // Find the organization details for the modal
    const org = organizations.find(o => o.id === orgId);
    if (!org) {
      setError('Organization not found');
      return;
    }
    
    // Set up for deletion and show modal
    setOrgToDelete(org);
    setShowOrgDeleteModal(true);
  };

  const confirmOrganizationDelete = async () => {
    if (!orgToDelete) return;
    
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.delete(`${apiBase}/admin/organizations/${orgToDelete.id}`, { headers });
      alert(`Organization "${orgToDelete.name}" has been permanently deleted.`);
      await loadData();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Failed to delete organization:', err);
      setError('Failed to delete organization: ' + (err.response?.data?.detail || err.message));
    } finally {
      setShowOrgDeleteModal(false);
      setOrgToDelete(null);
    }
  };

  const cancelOrganizationDelete = () => {
    setShowOrgDeleteModal(false);
    setOrgToDelete(null);
  };

  const handleReinstateOrganization = async (orgId) => {
    if (window.confirm('Are you sure you want to reinstate this organization? This will restore all users and data.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.post(`${apiBase}/admin/organizations/${orgId}/reinstate`, {}, { headers });
        alert('Organization reinstated successfully!');
        await loadData();
      } catch (err) {
        setError('Failed to reinstate organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handlePermanentlyDelete = async (orgId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this organization? This action cannot be undone and will remove all data forever.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.delete(`${apiBase}/admin/organizations/${orgId}/permanent`, { headers });
        alert('Organization permanently deleted.');
        await loadData();
      } catch (err) {
        setError('Failed to permanently delete organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleRemoveUser = async (userId) => {
    // Find the user details for the confirmation modal
    const user = users.find(u => u.id === userId);
    if (!user) {
      console.error('User not found for deletion:', userId);
      setError('User not found - cannot remove');
      return;
    }

    // Set up for deletion and show custom confirmation modal
    setUserToDelete(user);
    setShowUserDeleteModal(true);
  };

  const confirmUserDelete = async () => {
    if (!userToDelete) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.delete(`${apiBase}/admin/users/${userToDelete.id}`, { headers });

      // Remove the deleted user from local state
      const updatedUsers = users.filter(user => user.id !== userToDelete.id);

      setUsers(updatedUsers);
      setError('');

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50';
      successMessage.textContent = `User ${userToDelete.first_name} ${userToDelete.last_name} deactivated successfully`;
      document.body.appendChild(successMessage);
      
      // Remove success message after 3 seconds
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Delete user error:', error);
      let errorMessage = 'Failed to remove user';
      
      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You cannot remove this user.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found or already removed.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteModal(false);
    setUserToDelete(null);
  };

  const handleReactivateUser = async (userId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/users/${userId}/reactivate`, {}, { headers });

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50';
      successMessage.textContent = 'User reactivated successfully';
      document.body.appendChild(successMessage);
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);

      loadData(); // Reload to get updated user list
    } catch (err) {
      setError('Failed to reactivate user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePermanentlyDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    if (!userToDelete) return;

    if (!window.confirm(`Are you sure you want to PERMANENTLY delete ${userToDelete.first_name} ${userToDelete.last_name} (${userToDelete.email})?\n\nThis action cannot be undone and will remove all user data forever.`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/users/${userId}/permanent`, { headers });

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50';
      successMessage.textContent = 'User permanently deleted';
      document.body.appendChild(successMessage);
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);

      loadData(); // Reload to get updated user list
    } catch (err) {
      setError('Failed to permanently delete user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/role`, { role: newRole }, { headers });
      loadData();
    } catch (err) {
      setError('Failed to update user role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmin = async (userId, isOrgAdmin) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/organization-admin`, 
        { is_organization_admin: isOrgAdmin }, 
        { headers }
      );
      loadData(); // Refresh user list
    } catch (err) {
      setError('Failed to update organization admin status: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">This area is restricted to superadmin users only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Superadmin Dashboard</h1>
            <p className="text-purple-100">Manage organizations, users, and system settings</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow p-1 overflow-x-auto">
        <div className="flex sm:grid sm:grid-cols-5 lg:grid-cols-11 gap-1 min-w-max sm:min-w-0">
          {[
            { id: 'organizations', label: 'Organizations', shortLabel: 'Orgs', icon: '🏢' },
            { id: 'packages', label: 'Packages', shortLabel: 'Pkg', icon: '📦' },
            { id: 'users', label: 'Users', shortLabel: 'Users', icon: '👥' },
            { id: 'invites', label: 'Invites', shortLabel: 'Inv', icon: '📧' },
            { id: 'parsing', label: 'Parsing Feedback', shortLabel: 'Parse', icon: '⚠️' },
            { id: 'flightaware', label: 'FlightAware', shortLabel: 'Flight', icon: '✈️' },
            { id: 'aiusage', label: 'AI Usage', shortLabel: 'AI', icon: '📊' },
            { id: 'rejected', label: 'Rejected Emails', shortLabel: 'Reject', icon: '🚫' },
            { id: 'domains', label: 'Domains', shortLabel: 'Dom', icon: '🌐' },
            { id: 'settings', label: 'Settings', shortLabel: 'Set', icon: '⚙️' },
            { id: 'deleted', label: 'Deleted Orgs', shortLabel: 'Del', icon: '🗑️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden lg:inline">{tab.label}</span>
              <span className="lg:hidden">{tab.shortLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'organizations' && (
        <OrganizationsTab
          organizations={organizations}
          packages={packages}
          onCreateOrganization={() => setShowCreateOrgModal(true)}
          onDeleteOrganization={handleDeleteOrganization}
          onManageAdmins={handleManageOrgAdmins}
          onComponentSettings={handleComponentSettings}
          onSSODomains={handleSSODomains}
          onAssignPackage={() => setShowAssignPackageModal(true)}
        />
      )}

      {activeTab === 'packages' && (
        <PackagesTab
          packages={packages}
          onCreatePackage={() => {
            setSelectedPackage(null);
            setShowPackageFormModal(true);
          }}
          onEditPackage={(pkg) => {
            setSelectedPackage(pkg);
            setShowPackageFormModal(true);
          }}
          onDeletePackage={handleDeletePackage}
          onAssignPackage={() => setShowAssignPackageModal(true)}
        />
      )}

      {activeTab === 'users' && (
        <UsersTab
          users={users}
          organizations={organizations}
          onInviteUser={() => setShowInviteUserModal(true)}
          onRemoveUser={handleRemoveUser}
          onUpdateUserRole={handleUpdateUserRole}
          onUpdateOrgAdmin={handleUpdateOrgAdmin}
          showInactiveUsers={showInactiveUsers}
          onToggleInactiveUsers={() => setShowInactiveUsers(!showInactiveUsers)}
          onReactivateUser={handleReactivateUser}
          onPermanentlyDeleteUser={handlePermanentlyDeleteUser}
        />
      )}

      {activeTab === 'invites' && (
        <InvitesTab
          invites={invites}
          onApproveInvite={handleApproveInvite}
          onRejectInvite={handleRejectInvite}
        />
      )}

      {activeTab === 'parsing' && (
        <ParsingFeedbackAdmin />
      )}

      {activeTab === 'flightaware' && (
        <FlightAwareLogs />
      )}

      {activeTab === 'aiusage' && (
        <AIUsageLogs />
      )}

      {activeTab === 'rejected' && (
        <RejectedEmailLogs />
      )}

      {activeTab === 'domains' && (
        <DomainsTab
          domains={emailDomains}
          onRefresh={loadData}
          onError={setError}
          setDomains={setEmailDomains}
        />
      )}

      {activeTab === 'settings' && (
        <SystemSettingsTab
          settings={systemSettings}
          onUpdate={setSystemSettings}
          onError={setError}
        />
      )}

      {activeTab === 'deleted' && (
        <DeletedOrganizationsTab
          deletedOrganizations={deletedOrganizations}
          onReinstateOrganization={handleReinstateOrganization}
          onPermanentlyDelete={handlePermanentlyDelete}
          onRefresh={loadData}
        />
      )}

      {/* Modals */}
      {showCreateOrgModal && (
        <CreateOrganizationModal
          users={users}
          onClose={() => setShowCreateOrgModal(false)}
          onCreate={handleCreateOrganization}
        />
      )}

      {showInviteUserModal && (
        <InviteUserModal
          organizations={organizations}
          onClose={() => setShowInviteUserModal(false)}
          onInvite={handleInviteUser}
        />
      )}

      {showInviteLinkModal && inviteResult && (
        <InviteLinkModal
          inviteLink={inviteResult.inviteLink}
          inviteToken={inviteResult.inviteToken}
          email={inviteResult.email}
          organizationName={inviteResult.organizationName}
          role={inviteResult.role}
          emailSentAutomatically={inviteResult.emailSent}
          onClose={() => {
            setShowInviteLinkModal(false);
            setInviteResult(null);
          }}
          onSendEmail={null} // We'll implement email sending later if needed
        />
      )}

      {showUserDeleteModal && userToDelete && (
        <UserDeleteModal
          user={userToDelete}
          onConfirm={confirmUserDelete}
          onCancel={cancelUserDelete}
        />
      )}

      {showOrgDeleteModal && orgToDelete && (
        <OrganizationDeleteModal
          organization={orgToDelete}
          onConfirm={confirmOrganizationDelete}
          onCancel={cancelOrganizationDelete}
        />
      )}

      {showManageAdminsModal && selectedOrganization && (
        <ManageOrgAdminsModal
          organization={selectedOrganization}
          onClose={() => {
            setShowManageAdminsModal(false);
            setSelectedOrganization(null);
          }}
          onUpdate={handleUpdateOrgAdmins}
        />
      )}

      {showComponentSettingsModal && selectedOrgForSettings && (
        <OrganizationComponentSettingsModal
          organization={selectedOrgForSettings}
          onClose={() => {
            setShowComponentSettingsModal(false);
            setSelectedOrgForSettings(null);
          }}
          onUpdate={() => {
            // Optionally refresh organization data if needed
            loadData();
          }}
        />
      )}

      {showSSODomainModal && selectedOrgForSSO && (
        <SSODomainModal
          organization={selectedOrgForSSO}
          currentUser={user}
          onClose={() => {
            setShowSSODomainModal(false);
            setSelectedOrgForSSO(null);
            loadData(); // Reload data to refresh any changes
          }}
        />
      )}

      {/* Package Modals */}
      <PackageFormModal
        isOpen={showPackageFormModal}
        onClose={() => {
          setShowPackageFormModal(false);
          setSelectedPackage(null);
        }}
        onSave={handleSavePackage}
        existingPackage={selectedPackage}
      />

      <AssignPackageModal
        isOpen={showAssignPackageModal}
        onClose={() => setShowAssignPackageModal(false)}
        onSuccess={() => {
          loadData();
          setShowAssignPackageModal(false);
        }}
        organizations={organizations}
        packages={packages}
      />
    </div>
  );
};

// Organizations Tab Component
const OrganizationsTab = ({ organizations, packages, onCreateOrganization, onDeleteOrganization, onManageAdmins, onComponentSettings, onSSODomains, onAssignPackage }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Organizations</h2>
        <p className="text-gray-600 text-sm sm:text-base">Manage organizations and their settings</p>
      </div>
      <button
        onClick={onCreateOrganization}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base whitespace-nowrap"
      >
        + Create Organization
      </button>
    </div>

    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Domain</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Trips</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
            <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {organizations.map((org) => (
            <tr key={org.id} className="hover:bg-gray-50">
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{org.name}</div>
                  <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[150px] sm:max-w-none">{org.description || 'No description'}</div>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                {org.domain || 'No domain'}
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {org.user_count}
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                {org.active_trips}
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  org.auto_approval ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {org.auto_approval ? 'Auto' : 'Manual'}
                </span>
              </td>
              <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex flex-row space-x-1 sm:space-x-2 justify-center">
                  <button
                    onClick={() => onComponentSettings(org)}
                    className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors"
                    title="Component Settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onSSODomains(org)}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                    title="Google SSO Domains"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onManageAdmins(org)}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                    title="Manage Admins"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDeleteOrganization(org.id)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete Organization"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Users Tab Component
const UsersTab = ({ users, organizations, onInviteUser, onRemoveUser, onUpdateUserRole, onUpdateOrgAdmin, showInactiveUsers, onToggleInactiveUsers, onReactivateUser, onPermanentlyDeleteUser }) => {
  const inactiveUserCount = users.filter(u => u.is_active === false).length;
  const activeUserCount = users.filter(u => u.is_active !== false).length;

  return (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Users</h2>
        <p className="text-gray-600 text-sm sm:text-base">
          Manage users across all organizations
          {showInactiveUsers && inactiveUserCount > 0 && (
            <span className="ml-2 text-gray-500">
              ({activeUserCount} active, {inactiveUserCount} inactive)
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-4">
        <label className={`flex items-center text-sm cursor-pointer px-3 py-2 rounded-md transition-colors ${
          showInactiveUsers ? 'bg-orange-100 text-orange-800' : 'text-gray-600 hover:bg-gray-100'
        }`}>
          <input
            type="checkbox"
            checked={showInactiveUsers}
            onChange={onToggleInactiveUsers}
            className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 mr-2"
          />
          Show inactive users
        </label>
        <button
          onClick={onInviteUser}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base whitespace-nowrap"
        >
          + Invite User
        </button>
      </div>
    </div>

    {/* Inactive users info banner */}
    {showInactiveUsers && inactiveUserCount > 0 && (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-orange-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-orange-800">
              {inactiveUserCount} Inactive User{inactiveUserCount !== 1 ? 's' : ''} Found
            </h3>
            <div className="mt-1 text-sm text-orange-700">
              <p>Inactive users are shown with a grayed-out row and cannot log in.</p>
              <p className="mt-1">
                <strong>To deactivate a user:</strong> Click the trash icon next to an active user.
              </p>
              <p className="mt-1">
                <strong>To reactivate:</strong> Click the refresh icon. <strong>To permanently delete:</strong> Click the red trash icon (removes all data).
              </p>
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Organization</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
            <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16 sm:w-24">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className={`hover:bg-gray-50 ${user.is_active === false ? 'bg-gray-100 opacity-60' : ''}`}>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                    {user.is_active === false && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 sm:hidden">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[120px] sm:max-w-none">{user.email}</div>
                  <div className="text-xs text-gray-400 sm:hidden">{organizations.find(org => org.id === user.organization_id)?.name}</div>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                {organizations.find(org => org.id === user.organization_id)?.name || user.organization_id}
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div className="space-y-2">
                  <select
                    value={user.role}
                    onChange={(e) => onUpdateUserRole(user.id, e.target.value)}
                    className="text-xs sm:text-sm border border-gray-300 rounded px-1 sm:px-2 py-1 w-full max-w-[100px] sm:max-w-none"
                    disabled={user.is_active === false}
                  >
                    <option value="ea">EA</option>
                    <option value="executive">Executive</option>
                    <option value="other">Other</option>
                  </select>

                  {/* Organization Admin Checkbox */}
                  <label className="flex items-center text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={user.is_organization_admin || false}
                      onChange={(e) => onUpdateOrgAdmin(user.id, e.target.checked)}
                      className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-1"
                      disabled={user.is_active === false}
                    />
                    <span className="hidden sm:inline">Organization </span>Admin
                  </label>
                </div>
              </td>
              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell">
                {user.is_active === false ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                    Inactive
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                )}
              </td>
              <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex justify-center gap-1">
                  {user.is_active === false ? (
                    <>
                      <button
                        onClick={() => onReactivateUser && onReactivateUser(user.id)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                        title="Reactivate User"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onPermanentlyDeleteUser && onPermanentlyDeleteUser(user.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Permanently Delete User"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => onRemoveUser(user.id)}
                      className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-md transition-colors"
                      title="Deactivate User"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
};

// Invites Tab Component
const InvitesTab = ({ invites, onApproveInvite, onRejectInvite }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-xl font-bold text-gray-900">Pending Invites</h2>
      <p className="text-gray-600 text-sm sm:text-base">Review and approve user invitations</p>
    </div>

    <div className="bg-white rounded-lg shadow overflow-x-auto">
      {invites.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-4xl sm:text-6xl mb-4">📧</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pending invites</h3>
          <p className="text-gray-500 text-sm sm:text-base">All invitations have been processed</p>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Organization</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Invited By</th>
              <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invites.map((invite) => (
              <tr key={invite.id} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">{invite.email}</div>
                  <div className="text-xs text-gray-400 sm:hidden">{invite.organization_name}</div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                  {invite.organization_name}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {invite.role}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                  {invite.invited_by_name}
                </td>
                <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex flex-row space-x-1 sm:space-x-2 justify-center">
                    <button
                      onClick={() => onApproveInvite(invite.id)}
                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                      title="Approve Invite"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onRejectInvite(invite.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                      title="Reject Invite"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// Deleted Organizations Tab Component
const DeletedOrganizationsTab = ({ deletedOrganizations, onReinstateOrganization, onPermanentlyDelete, onRefresh }) => {
  const formatTimeRemaining = (reinstateBeforeDate) => {
    const now = new Date();
    const deadline = new Date(reinstateBeforeDate);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "1 day";
    return `${diffDays} days`;
  };

  const formatDeletedDate = (deletedAtDate) => {
    return new Date(deletedAtDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Deleted Organizations</h2>
          <p className="text-gray-600 text-sm sm:text-base">Organizations pending deletion (30-day retention period)</p>
        </div>
        <button
          onClick={onRefresh}
          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base whitespace-nowrap"
        >
          🔄 Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {deletedOrganizations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl sm:text-6xl mb-4">🗑️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No deleted organizations</h3>
            <p className="text-gray-500 text-sm sm:text-base">Organizations deleted in the last 30 days will appear here</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Deleted At</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Deleted By</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Time Left</th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deletedOrganizations.map((org) => {
                const timeRemaining = formatTimeRemaining(org.reinstate_before);
                const isExpired = timeRemaining === "Expired";

                return (
                  <tr key={org.id} className={`hover:bg-gray-50 ${isExpired ? 'bg-red-50' : ''}`}>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{org.name}</div>
                        <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[120px] sm:max-w-none">{org.description || 'No description'}</div>
                        {org.domain && (
                          <div className="text-xs text-gray-400 hidden sm:block">Domain: {org.domain}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                      {formatDeletedDate(org.deleted_at)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden lg:table-cell">
                      {org.deleted_by_name || 'Unknown'}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isExpired
                          ? 'bg-red-100 text-red-800'
                          : timeRemaining.includes('1 day') || timeRemaining.includes('2 day') || timeRemaining.includes('3 day')
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-800'
                      }`}>
                        {isExpired ? 'Expired' : `${timeRemaining}`}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-row space-x-1 sm:space-x-2 justify-center">
                        {!isExpired ? (
                          <button
                            onClick={() => onReinstateOrganization(org.id)}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                            title="Reinstate Organization"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        ) : (
                          <div className="p-2 text-gray-400" title="Cannot reinstate (expired)">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                            </svg>
                          </div>
                        )}
                        <button
                          onClick={() => onPermanentlyDelete(org.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Forever"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      
      {deletedOrganizations.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Retention Policy</h3>
              <div className="mt-1 text-sm text-yellow-700">
                <p>• Organizations are retained for 30 days after deletion</p>
                <p>• All users and data are preserved during this period</p>
                <p>• Organizations can be reinstated at any time before the deadline</p>
                <p>• After 30 days, data will be permanently deleted and cannot be recovered</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Packages Tab Component
const PackagesTab = ({ packages, onCreatePackage, onEditPackage, onDeletePackage, onAssignPackage }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Packages</h2>
        <p className="text-gray-600 text-sm sm:text-base">Manage subscription packages and assign them to organizations</p>
      </div>
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <button
          onClick={onAssignPackage}
          className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-md font-medium text-sm"
        >
          Assign Package
        </button>
        <button
          onClick={onCreatePackage}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-md font-medium text-sm"
        >
          + Create Package
        </button>
      </div>
    </div>

    <div className="bg-white rounded-lg shadow overflow-x-auto">
      {packages.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-4xl sm:text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No packages yet</h3>
          <p className="text-gray-500 text-sm sm:text-base">Create your first package to get started</p>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Features</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Assignments</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
              <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {packages.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{pkg.name}</div>
                    <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[100px] sm:max-w-[150px]">{pkg.description || 'No description'}</div>
                    {pkg.is_trial && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                        Trial
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {pkg.base_price > 0 ? `${pkg.base_price} ${pkg.currency}` : 'Free'}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {pkg.teams_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Teams</span>
                    )}
                    {pkg.flight_alerts_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">Alerts</span>
                    )}
                    {pkg.sso_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">SSO</span>
                    )}
                    {pkg.dedicated_emails_executives && (
                      <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded">Exec Email</span>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden lg:table-cell">
                  <span className="text-green-600 font-medium">{pkg.active_assignments || 0}</span>
                  <span className="text-gray-400"> / {pkg.total_assignments || 0}</span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    pkg.status === 'active' ? 'bg-green-100 text-green-800' :
                    pkg.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {pkg.status}
                  </span>
                </td>
                <td className="px-3 sm:px-4 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex flex-row space-x-1 sm:space-x-2 justify-center">
                    <button
                      onClick={() => onEditPackage(pkg)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                      title="Edit Package"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {pkg.status !== 'archived' && (
                      <button
                        onClick={() => onDeletePackage(pkg.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                        title="Archive Package"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

// Domains Tab Component
const DomainsTab = ({ domains, onRefresh, onError, setDomains }) => {
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newDomain, setNewDomain] = React.useState({ domain: '', label: '', description: '' });
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});

  // Load domains on mount if empty
  React.useEffect(() => {
    if (domains.length === 0) {
      loadDomains();
    }
  }, []);

  const loadDomains = async () => {
    try {
      const data = await listEmailDomains(true);
      setDomains(data);
    } catch (err) {
      console.error('Error loading domains:', err);
      onError('Failed to load domains: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.domain || !newDomain.label) return;
    setSaving(true);
    try {
      await addEmailDomain(newDomain);
      setNewDomain({ domain: '', label: '', description: '' });
      setShowAddForm(false);
      await loadDomains();
    } catch (err) {
      onError('Failed to add domain: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (domain) => {
    try {
      await updateEmailDomain(domain.id, { is_active: !domain.is_active });
      await loadDomains();
    } catch (err) {
      onError('Failed to update domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSetPrimary = async (domain) => {
    try {
      await setPrimaryDomain(domain.id);
      await loadDomains();
    } catch (err) {
      onError('Failed to set primary domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Are you sure you want to delete the domain "${domain.domain}"?`)) return;
    try {
      await deleteEmailDomain(domain.id);
      await loadDomains();
    } catch (err) {
      onError('Failed to delete domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleStartEdit = (domain) => {
    setEditingId(domain.id);
    setEditForm({ label: domain.label, description: domain.description || '' });
  };

  const handleSaveEdit = async (domain) => {
    try {
      await updateEmailDomain(domain.id, editForm);
      setEditingId(null);
      await loadDomains();
    } catch (err) {
      onError('Failed to update domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Email Domains</h2>
          <p className="text-gray-600 text-sm sm:text-base">
            Manage email domains for inbound email processing. Emails sent to any active domain will be processed the same way.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm sm:text-base whitespace-nowrap"
        >
          + Add Domain
        </button>
      </div>

      {/* Add Domain Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Domain</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                placeholder="e.g., new.veyage.io"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <input
                type="text"
                value={newDomain.label}
                onChange={(e) => setNewDomain({ ...newDomain, label: e.target.value })}
                placeholder="e.g., Veyage New"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <input
                type="text"
                value={newDomain.description}
                onChange={(e) => setNewDomain({ ...newDomain, description: e.target.value })}
                placeholder="Brief description of this domain's purpose"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => { setShowAddForm(false); setNewDomain({ domain: '', label: '', description: '' }); }}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddDomain}
              disabled={saving || !newDomain.domain || !newDomain.label}
              className={`px-4 py-2 rounded-md font-medium ${
                newDomain.domain && newDomain.label
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-xl">ℹ️</span>
          <div>
            <p className="text-sm text-blue-800">
              All active domains are equivalent for email processing. Emails sent to <strong>plans@veyage.io</strong> and{' '}
              <strong>plans@in.veyage.io</strong> are treated identically. The <strong>primary</strong> domain is used when
              creating new email addresses (e.g., for EA personal inboxes).
            </p>
          </div>
        </div>
      </div>

      {/* Domains Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Label</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {domains.map((domain) => (
              <tr key={domain.id} className={!domain.is_active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{domain.domain}</span>
                    {domain.is_primary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Primary
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                  {editingId === domain.id ? (
                    <input
                      type="text"
                      value={editForm.label}
                      onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-sm text-gray-700">{domain.label}</span>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                  {editingId === domain.id ? (
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-sm text-gray-500">{domain.description || '—'}</span>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(domain)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      domain.is_active ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    title={domain.is_active ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        domain.is_active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    {editingId === domain.id ? (
                      <>
                        <button
                          onClick={() => handleSaveEdit(domain)}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                          title="Save"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                          title="Cancel"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleStartEdit(domain)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {!domain.is_primary && domain.is_active && (
                          <button
                            onClick={() => handleSetPrimary(domain)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                            title="Set as Primary"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          </button>
                        )}
                        {!domain.is_primary && (
                          <button
                            onClick={() => handleDelete(domain)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {domains.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No email domains configured. Add a domain to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Last Updated Info */}
      {domains.length > 0 && domains[0].updated_at && (
        <div className="text-sm text-gray-500">
          Last updated: {new Date(domains[0].updated_at).toLocaleString()}
          {domains[0].updated_by_email && ` by ${domains[0].updated_by_email}`}
        </div>
      )}
    </div>
  );
};

// System Settings Tab Component
// Note: AI model settings have been moved to the dedicated "AI & Parsing Settings" page
// This tab now only manages Parsing Feedback Export settings
const SystemSettingsTab = ({ settings, onUpdate, onError }) => {
  const [saving, setSaving] = React.useState(false);
  const [localSettings, setLocalSettings] = React.useState(settings?.parsing_feedback || {
    s3_export_enabled: true,
    github_commit_enabled: true
  });

  React.useEffect(() => {
    if (settings?.parsing_feedback) {
      setLocalSettings(settings.parsing_feedback);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.put(
        `${apiBase}/admin/system-settings`,
        {
          parsing_feedback: localSettings
        },
        { headers }
      );

      onUpdate(response.data);
    } catch (err) {
      console.error('Error saving system settings:', err);
      onError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const hasChanges = settings?.parsing_feedback &&
    (localSettings.s3_export_enabled !== settings.parsing_feedback.s3_export_enabled ||
     localSettings.github_commit_enabled !== settings.parsing_feedback.github_commit_enabled);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Export Settings</h2>
          <p className="text-gray-600">Configure parsing feedback export options</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
            hasChanges
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {/* Parsing Feedback Export Settings */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Parsing Feedback Export</h3>
          <p className="text-sm text-gray-500 mt-1">Configure how parsing feedback is exported for Claude Code analysis</p>
        </div>
        <div className="p-6 space-y-6">
          {/* S3 Export Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl">☁️</span>
                <h4 className="text-sm font-medium text-gray-900">S3 Export (Presigned URLs)</h4>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-9">
                Export feedback with S3 presigned URLs for document access. URLs expire after 24 hours.
              </p>
            </div>
            <button
              onClick={() => toggleSetting('s3_export_enabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSettings.s3_export_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSettings.s3_export_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* GitHub Commit Toggle */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-6">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📦</span>
                <h4 className="text-sm font-medium text-gray-900">GitHub Commit (Send to Claude Code)</h4>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-9">
                Commit feedback documents directly to GitHub repository for Claude Code access. Recommended for Claude Code workflows.
              </p>
            </div>
            <button
              onClick={() => toggleSetting('github_commit_enabled')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSettings.github_commit_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSettings.github_commit_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Warning if both are disabled */}
          {!localSettings.s3_export_enabled && !localSettings.github_commit_enabled && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2 text-yellow-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-medium">Warning:</span>
                <span>Both export methods are disabled. You won't be able to export parsing feedback.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Settings Info Box - points to new settings page */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h3 className="text-sm font-medium text-blue-900">AI & Parsing Settings</h3>
            <p className="text-sm text-blue-700 mt-1">
              AI model configuration has moved to the dedicated{' '}
              <a href="/superadmin-settings" className="font-medium underline hover:text-blue-800">
                AI & Parsing Settings
              </a>{' '}
              page under Parsing & Data menu.
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated Info */}
      {settings?.updated_at && (
        <div className="text-sm text-gray-500">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by_user_email && ` by ${settings.updated_by_user_email}`}
        </div>
      )}
    </div>
  );
};

export default SuperadminDashboard;